import { createContext, useContext, useState, useEffect } from "react";
import axios from "../axios";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading=true

  // Fetch the authenticated user on app load
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get('/api/user');
        setUser(data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []); // Dependency array ensures this runs only once


  const login = async (email, password) => {
    setLoading(true);
    try {
      await axios.get("/sanctum/csrf-cookie"); // Fetch CSRF token
      const response = await axios.post("/login", { email, password });
      const token = response.data.access_token;

      // Save token and set headers for future requests
      localStorage.setItem("auth_token", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Fetch user data
      const { data } = await axios.get("/api/user");
      setUser(data);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await axios.post("/logout");
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const generate2FASecret = async () => {
    try {
      const response = await axios.post("/api/generate-2fa-secret");
      console.log(response);
      return response.data;
    } catch (error) {
      console.error("Failed to generate 2FA secret:", error);
      throw error;
    }
  };

  const enable2FA = async (otp) => {
    try {
      await axios.post("/api/enable-2fa", { otp });
      const response = await axios.get("/api/user");
      setUser(response.data); // Update the user state
    } catch (error) {
      console.error("Failed to enable 2FA:", error);
      throw error;
    }
  };

  const validate2FA = async (otp) => {
    try {
      await axios.post("/api/verify-2fa", { otp });
      // Fetch the updated user details
      const response = await axios.get("/api/user");
      setUser(response.data); // Update the user state
    } catch (error) {
      console.error("Failed to validate 2FA:", error);
      throw error;
    }
  };

  const send2FAQRCode = async (email) => {
    try {
      const response = await axios.post("/api/send-qr-code-link", {
        email, // Pass the email as a parameter in the request body
      });
      return response.data.message; // Return the success message
    } catch (error) {
      console.error("Failed to send 2FA QR code:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        generate2FASecret,
        enable2FA,
        validate2FA,
        send2FAQRCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
